import { useState, useEffect } from "react";
import { formatDE, normalizeDeLocal, validateDeLocal } from "src/helpers/sanitizeInput";
import "./phoneInput.scss";

export const COUNTRY_CODES = [
    { flag: "🇩🇪", name: "Deutschland",              dial: "+49"  },
    { flag: "🇦🇹", name: "Österreich",               dial: "+43"  },
    { flag: "🇨🇭", name: "Schweiz",                  dial: "+41"  },
    { flag: "🇦🇱", name: "Albanien",                 dial: "+355" },
    { flag: "🇦🇩", name: "Andorra",                  dial: "+376" },
    { flag: "🇧🇾", name: "Belarus",                  dial: "+375" },
    { flag: "🇧🇪", name: "Belgien",                  dial: "+32"  },
    { flag: "🇧🇦", name: "Bosnien und Herzegowina",  dial: "+387" },
    { flag: "🇧🇬", name: "Bulgarien",                dial: "+359" },
    { flag: "🇩🇰", name: "Dänemark",                 dial: "+45"  },
    { flag: "🇪🇪", name: "Estland",                  dial: "+372" },
    { flag: "🇫🇮", name: "Finnland",                 dial: "+358" },
    { flag: "🇫🇷", name: "Frankreich",               dial: "+33"  },
    { flag: "🇬🇷", name: "Griechenland",             dial: "+30"  },
    { flag: "🇮🇪", name: "Irland",                   dial: "+353" },
    { flag: "🇮🇸", name: "Island",                   dial: "+354" },
    { flag: "🇮🇹", name: "Italien",                  dial: "+39"  },
    { flag: "🇽🇰", name: "Kosovo",                   dial: "+383" },
    { flag: "🇭🇷", name: "Kroatien",                 dial: "+385" },
    { flag: "🇱🇻", name: "Lettland",                 dial: "+371" },
    { flag: "🇱🇮", name: "Liechtenstein",            dial: "+423" },
    { flag: "🇱🇹", name: "Litauen",                  dial: "+370" },
    { flag: "🇱🇺", name: "Luxemburg",                dial: "+352" },
    { flag: "🇲🇹", name: "Malta",                    dial: "+356" },
    { flag: "🇲🇩", name: "Moldau",                   dial: "+373" },
    { flag: "🇲🇨", name: "Monaco",                   dial: "+377" },
    { flag: "🇲🇪", name: "Montenegro",               dial: "+382" },
    { flag: "🇳🇱", name: "Niederlande",              dial: "+31"  },
    { flag: "🇲🇰", name: "Nordmazedonien",           dial: "+389" },
    { flag: "🇳🇴", name: "Norwegen",                 dial: "+47"  },
    { flag: "🇵🇱", name: "Polen",                    dial: "+48"  },
    { flag: "🇵🇹", name: "Portugal",                 dial: "+351" },
    { flag: "🇷🇴", name: "Rumänien",                 dial: "+40"  },
    { flag: "🇸🇲", name: "San Marino",               dial: "+378" },
    { flag: "🇸🇪", name: "Schweden",                 dial: "+46"  },
    { flag: "🇷🇸", name: "Serbien",                  dial: "+381" },
    { flag: "🇸🇰", name: "Slowakei",                 dial: "+421" },
    { flag: "🇸🇮", name: "Slowenien",               dial: "+386" },
    { flag: "🇪🇸", name: "Spanien",                  dial: "+34"  },
    { flag: "🇨🇿", name: "Tschechien",               dial: "+420" },
    { flag: "🇹🇷", name: "Türkei",                   dial: "+90"  },
    { flag: "🇺🇦", name: "Ukraine",                  dial: "+380" },
    { flag: "🇭🇺", name: "Ungarn",                   dial: "+36"  },
    { flag: "🇬🇧", name: "Vereinigtes Königreich",   dial: "+44"  },
    { flag: "🇨🇾", name: "Zypern",                   dial: "+357" },
];

// Sort by dial code length descending so +357 matches before +35
const SORTED_CODES = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);

function parse(value: unknown): { dial: string; local: string } {
    const v = (typeof value === "string" ? value : value == null ? "" : String(value)).trim();
    if (v.startsWith("+")) {
        const match = SORTED_CODES.find((c) => v.startsWith(c.dial));
        if (match) return { dial: match.dial, local: v.slice(match.dial.length) };
    }
    return { dial: "+49", local: v };
}

interface Props {
    value: any;
    onChange: (v: string) => void;
    className?: string;
    id?: string;
    disabled?: boolean;
}

export default function PhoneInput({ value, onChange, className, id, disabled }: Props) {
    const parsed = parse(value);
    const [dial, setDial]   = useState(parsed.dial);
    const [local, setLocal] = useState(parsed.local);
    const [error, setError] = useState<string | null>(null);

    // Keep internal state in sync when the parent value changes (e.g. on load)
    useEffect(() => {
        const p = parse(value);
        setDial(p.dial);
        const digits = p.local.replace(/\D/g, "");
        setLocal(p.dial === "+49" && digits ? formatDE(digits) : p.local);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const emit = (d: string, l: string) => {
        const trimmed = l.replace(/\D/g, "");
        onChange(trimmed ? `${d}${trimmed}` : "");
    };

    const handleDialChange = (d: string) => {
        setDial(d);
        setError(null);
        emit(d, local);
    };

    const handleLocalChange = (raw: string) => {
        // Allow free typing (including spaces the user types), but emit clean digits
        setLocal(raw);
        setError(null);
        emit(dial, raw);
    };

    const handleLocalBlur = () => {
        if (!local.trim()) { setLocal(""); setError(null); return; }

        if (dial === "+49") {
            // Normalize: strip any country-code prefix the user may have typed, strip trunk 0
            const normalized = normalizeDeLocal(local);
            const err = validateDeLocal(normalized);
            setError(err);
            setLocal(normalized ? formatDE(normalized) : "");
            // Emit the clean normalized digits
            onChange(normalized ? `+49${normalized}` : "");
        } else {
            const digits = local.replace(/\D/g, "");
            setLocal(digits);
            emit(dial, digits);
        }
    };

    const flag = COUNTRY_CODES.find((c) => c.dial === dial)?.flag ?? "";

    return (
        <div className={`phone-input-wrap${className ? ` ${className}` : ""}`}>
            <div className={`phone-input${error ? " phone-input--error" : ""}`}>
                <div className="phone-input__country">
                    <select
                        className="phone-input__dial"
                        value={dial}
                        onChange={(e) => handleDialChange(e.target.value)}
                        aria-label="Country code"
                        disabled={disabled}
                    >
                        {COUNTRY_CODES.map((c) => (
                            <option key={c.dial + c.name} value={c.dial}>
                                {c.flag} {c.dial} {c.name}
                            </option>
                        ))}
                    </select>
                    <span className="phone-input__flag" aria-hidden>{flag} {dial}</span>
                </div>
                <input
                    id={id}
                    className="phone-input__number"
                    type="tel"
                    value={local}
                    onChange={(e) => handleLocalChange(e.target.value)}
                    onBlur={handleLocalBlur}
                    placeholder="170 1234567"
                    autoComplete="tel-national"
                    disabled={disabled}
                />
            </div>
            {error && <span className="phone-input__error">{error}</span>}
        </div>
    );
}
